import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Plus, Check, ListMusic, Lock, Globe, Loader2 } from "lucide-react";

/**
 * Fenêtre « Ajouter à une playlist » : cocher/décocher les playlists du fan,
 * ou en créer une nouvelle avec ce titre dedans. Ouverte via usePlaylists().openPicker(track).
 */
export default function AddToPlaylistModal({ track, playlists, loaded, onCreate, onAdd, onRemove, onClose }) {
    const [creating, setCreating] = useState(false);
    const [title, setTitle] = useState("");
    const [isPublic, setIsPublic] = useState(true);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        if (!track) return;
        setTitle("");
        setIsPublic(true);
        // Aucune playlist encore : on propose directement d'en créer une.
        setCreating(loaded && playlists.length === 0);
    }, [track]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!track) return undefined;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [track, onClose]);

    if (!track) return null;

    const toggle = async (pl) => {
        setBusyId(pl.id);
        if ((pl.track_ids || []).includes(track.id)) await onRemove(pl.id, track.id);
        else await onAdd(pl.id, track);
        setBusyId(null);
    };

    const submitNew = async (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        setBusyId("new");
        const pl = await onCreate(title.trim(), track.id, { isPublic });
        setBusyId(null);
        if (pl) onClose();
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center sm:px-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="add-to-playlist-modal">
            <div
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Ajouter à une playlist"
                className="relative w-full sm:max-w-md bg-[#161616] border border-[#262626] rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[85vh] flex flex-col"
            >
                <div className="flex items-center gap-3 p-5 border-b border-[#262626]">
                    {track.cover_url
                        ? <img src={track.cover_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                        : <div className="w-12 h-12 rounded-lg bg-[#262626] shrink-0" />}
                    <div className="min-w-0 flex-1">
                        <p className="text-xs uppercase tracking-widest text-[#00d97e] font-bold">Ajouter à une playlist</p>
                        <p className="text-sm font-medium truncate mt-0.5">{track.title}</p>
                        <p className="text-xs text-[#6E6E6E] truncate">{track.artist_name}</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[#262626] text-[#A8A8A8]" aria-label="Fermer" data-testid="add-to-playlist-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="overflow-y-auto p-3 flex-1">
                    {!loaded && <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#00d97e]" /></div>}
                    {loaded && playlists.map((pl) => {
                        const inside = (pl.track_ids || []).includes(track.id);
                        return (
                            <button
                                key={pl.id}
                                onClick={() => toggle(pl)}
                                disabled={busyId === pl.id}
                                className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-[#1c1c1c] text-left disabled:opacity-60"
                                data-testid={`add-to-playlist-item-${pl.id}`}
                            >
                                <PlaylistThumb covers={pl.covers} size="w-11 h-11" />
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium truncate">{pl.title}</p>
                                    <p className="text-xs text-[#6E6E6E] flex items-center gap-1">
                                        {pl.is_public ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                                        {pl.track_count} titre{pl.track_count > 1 ? "s" : ""}
                                    </p>
                                </div>
                                <span className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 ${inside ? "bg-[#00d97e] border-[#00d97e] text-[#0a0a0a]" : "border-[#3a3a3a]"}`}>
                                    {busyId === pl.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : inside && <Check className="w-3.5 h-3.5" />}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="p-4 border-t border-[#262626]">
                    {creating ? (
                        <form onSubmit={submitNew} className="space-y-3" data-testid="new-playlist-form">
                            <input
                                autoFocus
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                maxLength={80}
                                placeholder="Nom de la playlist (ex. Ambiance du dimanche)"
                                className="w-full bg-[#0f0f0f] border border-[#262626] focus:border-[#00d97e] rounded-xl px-4 py-3 text-sm outline-none"
                                data-testid="new-playlist-title"
                            />
                            <label className="flex items-center gap-2 text-xs text-[#A8A8A8] cursor-pointer select-none">
                                <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="accent-[#00d97e]" />
                                Publique : visible et partageable avec un lien
                            </label>
                            <div className="flex gap-2">
                                {playlists.length > 0 && (
                                    <button type="button" onClick={() => setCreating(false)} className="flex-1 px-4 py-2.5 rounded-full border border-[#262626] text-sm text-[#A8A8A8] hover:text-[#F4F4F4]">
                                        Annuler
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    disabled={!title.trim() || busyId === "new"}
                                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#00d97e] text-[#0a0a0a] text-sm font-semibold disabled:opacity-50"
                                    data-testid="new-playlist-submit"
                                >
                                    {busyId === "new" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                    Créer et ajouter
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="flex items-center justify-between gap-3">
                            <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 text-sm font-medium text-[#00d97e] hover:underline" data-testid="new-playlist-open">
                                <Plus className="w-4 h-4" /> Nouvelle playlist
                            </button>
                            <Link to="/me/playlists" onClick={onClose} className="text-xs text-[#A8A8A8] hover:text-[#F4F4F4]">
                                Gérer mes playlists →
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/** Mosaïque de 1 à 4 pochettes, ou une icône si la playlist est vide. */
export function PlaylistThumb({ covers = [], size = "w-12 h-12", rounded = "rounded-lg" }) {
    if (!covers.length) {
        return (
            <div className={`${size} ${rounded} bg-gradient-to-br from-[#00d97e]/25 to-[#ffc83d]/25 flex items-center justify-center shrink-0`}>
                <ListMusic className="w-1/2 h-1/2 text-[#F4F4F4]/80" />
            </div>
        );
    }
    if (covers.length < 4) {
        return <img src={covers[0]} alt="" loading="lazy" className={`${size} ${rounded} object-cover shrink-0`} />;
    }
    return (
        <div className={`${size} ${rounded} overflow-hidden grid grid-cols-2 grid-rows-2 shrink-0`}>
            {covers.slice(0, 4).map((c) => <img key={c} src={c} alt="" loading="lazy" className="w-full h-full object-cover" />)}
        </div>
    );
}
